import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import axios from 'axios';
import classNames from 'classnames';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, {
    useCallback,
    useContext,
    useEffect,
    useState,
    useRef,
} from 'react';
import { toast } from 'react-toastify';

import {
    MCO_THEME,
    POL_THEME,
    ThemeContext,
} from '../../constants/theme';
import customerDataRequest from '../../services/api/customer-data';
import ContactType from '../../types/ContactType';
import { getSimpleFormattedTime } from '../../utils/date';
import { getDaysInOrder } from '../../utils/days';

import themeStyles from '../../_theme.scss';
import styles from './MassTimeSummary.scss';

const removeEnglishFromLangStr = (languagesString) => languagesString.replace('English, ', '').replace('English', '');

const ADDRESS_THRESHOLD = 0.7;
const NAME_THRESHOLD = 0.3;
const DISTANCE_THRESHOLD = 0.1; // In miles
const MASSTIMES_TIMEOUT = 3000; // milliseconds
const TYPE_ORDER = ['Weekend', 'Weekday', 'Special', 'Confession'];

const MassTimeCategory = ({ type, data }) => {
    const sortedDataByDays = getDaysInOrder(data, 'day_of_week');
    const themeContext = useContext(ThemeContext);

    const weekdayContainerClass = themeContext === POL_THEME
        ? styles.weekdayContainerPOL
        : styles.weekdayContainerMCO;

    return (
        <React.Fragment key={`${type}-section`}>
            <div
                className={classNames(styles.weekdayContainer, weekdayContainerClass)}
                style={{ fontSize: '14px' }}
                key={`${type}-container`}
            >
                <div>
                    <Typography
                        key={type}
                        mb="0px"
                        sx={{
                            fontWeight: 'bold',
                            color: '#4E4E4E',
                        }}
                    >
                        {type}
                    </Typography>
                </div>
                {sortedDataByDays.map((d) => {
                    const languages = removeEnglishFromLangStr(d.languages);
                    const startTime = d.start_time ? getSimpleFormattedTime(d.start_time) : '';
                    const endTime = d.end_time ? getSimpleFormattedTime(d.end_time) : '';

                    return (
                        <div key={d.id}>
                            <div className={styles.timeEntryContainer}>
                                <Typography>
                                    {d.day_of_week}
                                    {': '}
                                    {startTime}
                                    {endTime && ` - ${endTime}`}
                                </Typography>
                                <div className={styles.languagesContainer}>
                                    <Typography>
                                        {languages.length > 0 && languages}
                                    </Typography>
                                </div>
                                <div className={styles.additionalDetailsContainer}>
                                    <Typography>
                                        {d.additional_details}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </React.Fragment>
    );
};

MassTimeCategory.propTypes = {
    type: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const MassTimesSummary = ({
    salesforceId,
    latitude,
    longitude,
    contactInfo,
    organizationName,
}) => {
    const theme = useTheme();
    const themeContext = useContext(ThemeContext);
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

    const [isLoading, setIsLoading] = useState(false);
    const [massTimesEntries, setMassTimesEntries] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [categoryCount, setCategoryCount] = useState(null);
    const [textContainer, setTextContainer] = useState('');
    const [massTimesDotOrgId, setMassTimesDotOrgId] = useState(null);
    const isFetching = useRef(false);

    const timesHeadlineCopy = themeContext === POL_THEME
        ? 'Mass Times'
        : 'Hours';
    const headerContainerClass = themeContext === POL_THEME
        ? styles.timesHeaderContainerPOL
        : styles.timesHeaderContainerMCO;

    useEffect(() => {
        if (themeContext === MCO_THEME) {
            if (categoryCount === 1) {
                setTextContainer(styles.textContainerSingle);
            } else {
                setTextContainer(themeStyles.textContainerMultiple);
            }
        }
    }, [categoryCount, themeContext]);

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Radius of the Earth in kilometers
        const toRadians = (deg) => (deg * Math.PI) / 180;

        const phi1 = toRadians(90 - lat1);
        const phi2 = toRadians(90 - lat2);
        const deltaLambda = toRadians(lon1 - lon2);

        const distanceInKm = R * Math.acos(Math.cos(phi1) * Math.cos(phi2)
            + Math.sin(phi1) * Math.sin(phi2) * Math.cos(deltaLambda));

        return (distanceInKm * 0.621371).toFixed(2); // Convert km to miles
    };

    const getStringSimilarity = (str1, str2) => {
        if (!str1 || !str2) return 0;
        const a = str1.length; const b = str2.length; const
            costs = Array(b + 1).fill(0);
        for (let j = 0; j <= b; j++) costs[j] = j;
        for (let i = 1; i <= a; i++) {
            let prevCost = i; let
                currCost;
            for (let j = 1; j <= b; j++) {
                currCost = str1[i - 1] === str2[j - 1] ? costs[j - 1] : Math.min(costs[j - 1], prevCost, costs[j]) + 1;
                costs[j - 1] = prevCost;
                prevCost = currCost;
            }
            costs[b] = prevCost;
        }
        return 1 - (costs[b] / Math.max(a, b)); // Normalized similarity
    };

    const getMassTimes = useCallback(async () => {
        if (isFetching.current) return; // Skip if already fetching

        const mapToInternalFormat = (input) => input.church_worship_times.map((time) => ({
            id: parseInt(time.id, 10),
            type: time.service_typename.replace('Week Days', 'Weekday'),
            day_of_week: time.day_of_week.trim(),
            start_time: time.time_start,
            end_time: time.time_end || null,
            languages: input.language_name.trim(),
            additional_details: time.comment || null,
            title: '',
            timezone: '',
            deleted_at: null,
            organization_salesforce_id: salesforceId,
            created_at: new Date().toISOString(), // Use current timestamp
            updated_at: null,
        }));

        isFetching.current = true;
        setIsLoading(true);

        try {
            let massTimesData;

            if (themeContext === POL_THEME && organizationName && latitude && longitude) {
                try {
                    const results = await axios.request({
                        url: `https://apiv4.updateparishdata.org/Churchs/?lat=${latitude}&long=${longitude}&pg=1`,
                        method: 'GET',
                        timeout: MASSTIMES_TIMEOUT,
                    });

                    if (results.data && results.data.length > 0) {
                        const firstResult = results.data[0];

                        const nameSimilarity = getStringSimilarity(organizationName, firstResult.name);

                        const formattedOrganizationAddress = `${contactInfo.listingAddress1}, ${contactInfo.listingCity}, ${contactInfo.listingState} ${contactInfo.listingZip}`;
                        const formattedMassTimesAddress = `${firstResult.church_address_street_address}, ${firstResult.church_address_city_name}, ${firstResult.church_address_providence_name} ${firstResult.church_address_postal_code}`;
                        const addressSimilarity = getStringSimilarity(formattedOrganizationAddress, formattedMassTimesAddress);

                        const distance = calculateDistance(latitude, longitude, firstResult.latitude, firstResult.longitude);

                        // If the first result has a name, address and distance that are within reasonable tolerances, we can assume it's the correct listing
                        if (nameSimilarity > NAME_THRESHOLD && addressSimilarity > ADDRESS_THRESHOLD && distance < DISTANCE_THRESHOLD) {
                            massTimesData = mapToInternalFormat(firstResult);
                            setMassTimesDotOrgId(firstResult.id);
                        } else {
                            // eslint-disable-next-line no-console
                            console.log('name similarity', nameSimilarity.toFixed(2));
                            // eslint-disable-next-line no-console
                            console.log('address similarity', addressSimilarity.toFixed(2));
                            // eslint-disable-next-line no-console
                            console.log('distance', distance);
                        }
                    }
                } catch (error) {
                }
            }

            if (!massTimesData) {
                const res = await customerDataRequest('get', `/organizations/${salesforceId}/mass-times`);
                massTimesData = res.data.data;
            }

            massTimesData.sort((a, b) => a.start_time.localeCompare(b.start_time));
            const groupedMassTimesByTypes = groupBy(massTimesData, 'type');
            const ordered = Object.keys(groupedMassTimesByTypes).sort((a, b) => {
                const indexA = TYPE_ORDER.indexOf(a);
                const indexB = TYPE_ORDER.indexOf(b);

                if (indexA === -1 && indexB === -1) return a.localeCompare(b); // Sort remaining alphabetically
                if (indexA === -1) return 1; // Move unknown types to the end
                if (indexB === -1) return -1;

                return indexA - indexB;
            }).reduce((obj, key) => {
                const typesCategory = obj;
                typesCategory[key] = groupedMassTimesByTypes[key];
                return typesCategory;
            }, {});
            // Getting the category count
            const count = Object.keys(groupedMassTimesByTypes).length;
            setMassTimesEntries(ordered);
            setCategoryCount(count);
        } catch (error) {
            toast.error(`There was an error loading ${themeContext === POL_THEME ? 'Mass Times' : 'Hours'}.`);
        } finally {
            isFetching.current = false;
            setIsLoading(false);
        }

        setIsLoading(false);
    }, [salesforceId, themeContext, contactInfo, organizationName, latitude, longitude]);

    const loadMassTime = useCallback(() => {
        if (salesforceId) {
            getMassTimes();
        }
    }, [salesforceId, getMassTimes]);

    useEffect(() => {
        loadMassTime();
    }, [loadMassTime]);

    const largeScreenView = (
        <>
            <div className={headerContainerClass}>
                <Box
                    key="massTimesTitle"
                    sx={{
                        flexGrow: 1,
                    }}
                >
                    <h2 className={styles.header}>
                        {timesHeadlineCopy}
                    </h2>
                </Box>
            </div>
            <div className={textContainer}>
                {massTimesEntries && Object.keys(massTimesEntries).map((el) => (
                    <MassTimeCategory key={el} type={el} data={massTimesEntries[el]} />
                ))}
            </div>
            {massTimesDotOrgId
                && (
                    <div className={classNames(styles.masstimesAttributionContainer)}>
                        <i>
                            Data provided by
                            {' '}
                            <a target="_blank" href="https://masstimes.org" rel="noreferrer">MassTimes.org</a>
                        </i>
                    </div>
                )}
        </>
    );

    const smallScreenView = (
        <>
            <MuiButton
                className={styles.viewTimesButton}
                fullWidth
                onClick={() => setIsModalOpen(true)}
                variant="contained"
            >
                {themeContext === POL_THEME ? 'View Mass Times' : 'View Hours'}
            </MuiButton>
            <Modal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            >
                <Box
                    className={styles.massTimesModal}
                    sx={{
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 2,
                    }}
                >
                    {largeScreenView}
                </Box>
            </Modal>
        </>
    );

    if (isLoading || isEmpty(massTimesEntries)) {
        return null;
    }

    if (!isLargeScreen) {
        return smallScreenView;
    }

    return largeScreenView;
};

MassTimesSummary.propTypes = {
    salesforceId: PropTypes.string,
    contactInfo: ContactType.isRequired,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    organizationName: PropTypes.string.isRequired,
};

MassTimesSummary.defaultProps = {
    salesforceId: undefined,
    latitude: undefined,
    longitude: undefined,
};

export default MassTimesSummary;
